import { css } from 'lit';
export const tagStyles = css `
    :host {
        --hlx-tag-padding-vertical-sm: 2px;
        --hlx-tag-padding-horizontal-sm: var(--hlx-spacing-xs);
        --hlx-tag-padding-vertical-md: var(--hlx-spacing-xs);
        --hlx-tag-padding-horizontal-md: var(--hlx-spacing-md);
    }

    .hlx-tag {
        display: inline-flex;
        align-items: center;
        border-radius: var(--hlx-radius-xs);
        gap: var(--hlx-spacing-2xs);
        transition: background-color var(--hlx-motion-duration-default)
            var(--hlx-motion-easing-in-out);
    }

    .hlx-tag-sm {
        padding: var(--hlx-tag-padding-vertical-sm) var(--hlx-tag-padding-horizontal-sm);
    }

    .hlx-tag-md {
        padding: var(--hlx-tag-padding-vertical-md) var(--hlx-tag-padding-horizontal-md);
    }

    .hlx-toggle {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border-width: 0;
    }

    .hlx-toggle:focus-visible + .hlx-tag {
        outline: 2px solid var(--hlx-color-brand-default);
        outline-offset: 2px;
    }

    .hlx-tag-type-button {
        appearance: none;
        border: none;
        background: none;
    }

    .hlx-tag-type-link {
        appearance: none;
        text-decoration: none;
    }

    .hlx-tag:focus-visible {
        outline: 2px solid var(--hlx-color-brand-default);
        outline-offset: 2px;
    }

    .hlx-tag:is(.hlx-tag-with-close-icon, .hlx-tag-type-text) {
        cursor: default;
    }

    .hlx-tag:not(.hlx-tag-with-close-icon, .hlx-tag-type-text) {
        cursor: pointer;
    }

    .hlx-tag:not(.hlx-tag-disabled) .hlx-close-icon {
        cursor: pointer;
    }

    .hlx-tag.hlx-tag-disabled:not(.hlx-tag-type-text) {
        cursor: not-allowed;
        color: var(--hlx-text-color-weak-2) !important;
        background-color: var(--hlx-color-neutral-10) !important;
    }

    .hlx-tag-text {
        display: block;
        font-family: var(--hlx-text-font-family-serif);
        font-size: var(--hlx-text-font-size-sm);
        line-height: var(--hlx-text-line-height-default);
    }

    /* Bold styles start */

    .hlx-tag-bold {
        color: var(--hlx-text-color-inverted-default);
        font-weight: var(--hlx-text-font-weight-medium);
    }

    .hlx-tag-bold.hlx-tag-brand {
        background-color: var(--hlx-color-brand-default);
    }

    .hlx-tag-bold.hlx-tag-brand:not(.hlx-tag-type-text):hover {
        background-color: var(--hlx-color-brand-weak-1);
    }

    .hlx-tag-bold.hlx-tag-brand:not(.hlx-tag-type-text):active {
        background-color: var(--hlx-color-brand-strong-1);
        color: var(--hlx-text-link-color-inverted-hover);
    }

    .hlx-tag-bold.hlx-tag-dark {
        background-color: var(--hlx-color-dark-default);
    }

    .hlx-tag-bold.hlx-tag-dark:not(.hlx-tag-type-text):hover {
        background-color: var(--hlx-color-dark-weak-1);
    }

    .hlx-tag-bold.hlx-tag-dark:not(.hlx-tag-type-text):active {
        background-color: var(--hlx-color-dark-strong-1);
        color: var(--hlx-text-link-color-inverted-hover);
    }

    .hlx-tag-bold.hlx-tag-success {
        background-color: var(--hlx-color-success-default);
    }

    .hlx-tag-bold.hlx-tag-error {
        background-color: var(--hlx-color-error-default);
    }

    .hlx-tag-bold.hlx-tag-warning {
        color: var(--hlx-text-color-default);
        background-color: var(--hlx-color-warning-default);
    }

    .hlx-tag-bold.hlx-tag-info {
        background-color: var(--hlx-color-info-default);
    }

    .hlx-tag-bold.hlx-tag-inverted {
        color: var(--hlx-text-link-color-default);
        background-color: var(--hlx-background-color-default);
    }

    .hlx-tag-bold.hlx-tag-inverted:not(.hlx-tag-type-text) {
        color: var(--hlx-text-color-default);
    }

    .hlx-tag-bold.hlx-tag-inverted:not(.hlx-tag-type-text):hover {
        color: var(--hlx-text-link-color-hover);
    }

    .hlx-tag-bold.hlx-tag-inverted:not(.hlx-tag-type-text):active {
        color: var(--hlx-text-link-color-active);
    }

    /* Bold styles end */

    /* Subtle styles start */

    .hlx-tag-subtle {
        color: var(--hlx-text-color-default);
        font-weight: var(--hlx-text-font-weight-normal);
        transition: border var(--hlx-motion-duration-default) var(--hlx-motion-easing-in-out);
    }

    .hlx-tag-sm.hlx-tag-subtle {
        /* Overwriting default padding because subtle tags have border (only inverted + selected style) */
        padding: calc(var(--hlx-tag-padding-vertical-sm) - 1px)
            calc(var(--hlx-tag-padding-horizontal-sm) - 1px);
    }

    .hlx-tag-md.hlx-tag-subtle {
        /* Overwriting default padding because subtle tags have border (only inverted + selected style) */
        padding: calc(var(--hlx-tag-padding-vertical-md) - 1px)
            calc(var(--hlx-tag-padding-horizontal-md) - 1px);
    }

    .hlx-tag-subtle.hlx-tag-disabled {
        border: 1px solid var(--hlx-color-neutral-10) !important;
    }

    .hlx-tag-subtle.hlx-tag-brand {
        background-color: var(--hlx-color-brand-weak-2);
        border: 1px solid var(--hlx-color-brand-weak-2);
    }

    .hlx-tag-subtle.hlx-tag-brand:not(.hlx-tag-type-text) {
        color: var(--hlx-text-link-color-default);
    }

    .hlx-tag-subtle.hlx-tag-brand:not(.hlx-tag-type-text, .hlx-tag-selected):hover {
        color: var(--hlx-text-link-color-inverted-default);
        background-color: var(--hlx-color-brand-weak-1);
        border: 1px solid var(--hlx-color-brand-weak-1);
    }

    .hlx-tag-subtle.hlx-tag-brand:not(.hlx-tag-type-text):active {
        color: var(--hlx-text-link-color-inverted-hover);
        background-color: var(--hlx-color-brand-strong-1);
        border: 1px solid var(--hlx-color-brand-strong-1);
    }

    .hlx-tag-subtle.hlx-tag-brand.hlx-tag-selected {
        color: var(--hlx-text-color-inverted-default);
        background-color: var(--hlx-color-brand-default);
        border: 1px solid var(--hlx-color-brand-default);
    }

    .hlx-tag-subtle.hlx-tag-dark {
        background-color: var(--hlx-color-dark-weak-2);
        border: 1px solid var(--hlx-color-dark-weak-2);
    }

    .hlx-tag-subtle.hlx-tag-dark:not(.hlx-tag-type-text, .hlx-tag-selected):hover {
        color: var(--hlx-text-link-color-inverted-default);
        background-color: var(--hlx-color-dark-weak-1);
        border: 1px solid var(--hlx-color-dark-weak-1);
    }

    .hlx-tag-subtle.hlx-tag-dark:not(.hlx-tag-type-text):active {
        color: var(--hlx-text-link-color-inverted-hover);
        background-color: var(--hlx-color-dark-strong-1);
        border: 1px solid var(--hlx-color-dark-strong-1);
    }

    .hlx-tag-subtle.hlx-tag-dark.hlx-tag-selected {
        color: var(--hlx-text-color-inverted-default);
        background-color: var(--hlx-color-dark-default);
        border: 1px solid var(--hlx-color-dark-default);
    }

    .hlx-tag-subtle.hlx-tag-success {
        background-color: var(--hlx-color-success-weak-1);
        border: 1px solid var(--hlx-color-success-weak-1);
    }

    .hlx-tag-subtle.hlx-tag-error {
        background-color: var(--hlx-color-error-weak-1);
        border: 1px solid var(--hlx-color-error-weak-1);
    }

    .hlx-tag-subtle.hlx-tag-warning {
        background-color: var(--hlx-color-warning-weak-1);
        border: 1px solid var(--hlx-color-warning-weak-1);
    }

    .hlx-tag-subtle.hlx-tag-info {
        background-color: var(--hlx-color-info-weak-1);
        border: 1px solid var(--hlx-color-info-weak-1);
    }

    .hlx-tag-subtle.hlx-tag-inverted {
        color: var(--hlx-text-link-color-default);
        background-color: var(--hlx-background-color-default);
        border: 1px solid var(--hlx-background-color-default);
    }

    .hlx-tag-subtle.hlx-tag-inverted:not(.hlx-tag-type-text) {
        color: var(--hlx-text-color-default);
    }

    .hlx-tag-subtle.hlx-tag-inverted:not(.hlx-tag-type-text, .hlx-tag-selected):hover {
        color: var(--hlx-text-link-color-hover);
    }

    .hlx-tag-subtle.hlx-tag-inverted:not(.hlx-tag-type-text):active {
        color: var(--hlx-text-link-color-active);
    }

    .hlx-tag-subtle.hlx-tag-inverted.hlx-tag-selected {
        color: var(--hlx-text-link-color-default);
        background-color: var(--hlx-color-brand-weak-2);
        border: 1px solid var(--hlx-color-brand-default);
    }

    /* Subtle styles end */

    /* Minimal styles start */

    .hlx-tag-minimal {
        color: var(--hlx-text-color-default);
        font-weight: var(--hlx-text-font-weight-normal);
    }

    .hlx-tag-sm.hlx-tag-minimal {
        /* Overwriting default padding because minimal tags have border */
        padding: calc(var(--hlx-tag-padding-vertical-sm) - 1px)
            calc(var(--hlx-tag-padding-horizontal-sm) - 1px);
    }

    .hlx-tag-md.hlx-tag-minimal {
        /* Overwriting default padding because minimal tags have border */
        padding: calc(var(--hlx-tag-padding-vertical-md) - 1px)
            calc(var(--hlx-tag-padding-horizontal-md) - 1px);
    }

    .hlx-tag-minimal.hlx-tag-disabled {
        border: 1px solid var(--hlx-color-neutral-10) !important;
    }

    .hlx-tag-minimal.hlx-tag-brand:not(.hlx-tag-type-text, .hlx-tag-selected) {
        color: var(--hlx-text-link-color-default);
    }

    .hlx-tag-minimal.hlx-tag-brand {
        border: 1px solid var(--hlx-color-brand-default);
    }

    .hlx-tag-minimal.hlx-tag-brand:not(.hlx-tag-type-text, .hlx-tag-selected):hover {
        background-color: var(--hlx-color-brand-weak-2);
    }

    .hlx-tag-minimal.hlx-tag-brand:not(.hlx-tag-type-text):active {
        color: var(--hlx-text-link-color-active);
        border-color: var(--hlx-color-brand-strong-1);
        background-color: var(--hlx-color-brand-weak-2);
    }

    .hlx-tag-minimal.hlx-tag-brand.hlx-tag-selected {
        color: var(--hlx-text-color-inverted-default);
        background-color: var(--hlx-color-brand-default);
        border: 1px solid var(--hlx-color-brand-default);
    }

    .hlx-tag-minimal.hlx-tag-dark {
        border: 1px solid var(--hlx-color-dark-default);
    }

    .hlx-tag-minimal.hlx-tag-dark:not(.hlx-tag-type-text, .hlx-tag-selected):hover {
        background-color: var(--hlx-color-dark-weak-2);
    }

    .hlx-tag-minimal.hlx-tag-dark:not(.hlx-tag-type-text):active {
        color: var(--hlx-color-dark-strong-1);
        border-color: var(--hlx-color-dark-strong-1);
        background-color: var(--hlx-color-dark-weak-2);
    }

    .hlx-tag-minimal.hlx-tag-dark.hlx-tag-selected {
        color: var(--hlx-text-color-inverted-default);
        background-color: var(--hlx-color-dark-default);
        border: 1px solid var(--hlx-color-dark-default);
    }

    .hlx-tag-minimal.hlx-tag-success {
        border: 1px solid var(--hlx-color-success-default);
    }

    .hlx-tag-minimal.hlx-tag-error {
        border: 1px solid var(--hlx-color-error-default);
    }

    .hlx-tag-minimal.hlx-tag-warning {
        border: 1px solid var(--hlx-color-warning-default);
    }

    .hlx-tag-minimal.hlx-tag-info {
        border: 1px solid var(--hlx-color-info-default);
    }

    .hlx-tag-minimal.hlx-tag-inverted {
        color: var(--hlx-text-color-inverted-default);
        border: 1px solid var(--hlx-background-color-default);
        background-color: var(--hlx-color-gray-100);
    }

    .hlx-tag-minimal.hlx-tag-inverted:not(.hlx-tag-type-text, .hlx-tag-selected):hover {
        background-color: var(--hlx-background-color-default);
        color: var(--hlx-text-link-color-hover);
    }

    .hlx-tag-minimal.hlx-tag-inverted:not(.hlx-tag-type-text):active {
        color: var(--hlx-text-link-color-active);
    }

    .hlx-tag-minimal.hlx-tag-inverted.hlx-tag-selected {
        color: var(--hlx-text-link-color-default);
        background-color: var(--hlx-color-brand-weak-2);
        border: 1px solid var(--hlx-color-brand-default);
    }

    /* Minimal styles end */
`;
//# sourceMappingURL=tag.styles.js.map